/**
 * GTM dataLayer transport.
 *
 * This is a sink, not a source. Every business event is defined once in `track-events.ts` against
 * the first-party pipeline, and calls in here only to additionally report itself to Google. Nothing
 * in this file decides what an event is or what it is called — GA4's own event names and payload
 * shapes are built by the caller, because Google mandates them (`view_item`, `purchase`, the
 * `ecommerce.items[]` shape) and they must not leak into the first-party schema.
 */

import { isTrackingGranted } from "./tracking-consent";
import { devLog } from "./dev-log";

/**
 * Pushes data to Google Tag Manager's dataLayer
 * @param data - The data object to push to dataLayer
 */
export const pushToDataLayer = (data: Record<string, any>) => {
  // Every push here carries identifiers or event parameters (user_id, product/order fields), which
  // APP-1182 forbids sending to Google before consent. Dropping the push — rather than trying to
  // strip fields — is what keeps GA4's pre-consent mode genuinely anonymous: GA4 still gets the
  // container's own cookieless page_view, and the first-party event is unaffected because it was
  // already sent by the caller before this.
  if (typeof window !== "undefined" && window.dataLayer && isTrackingGranted()) {
    window.dataLayer.push(data);
    devLog("📊 GTM DataLayer Event:", data);
  }
};
